import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { 
  AdminFAQService,
  type FAQResponse,
  type FAQListResponse,
  type FAQCreateRequest,
  type FAQUpdateRequest,
  type ApiError 
} from '@/shared/api'

// === FAQ HOOKS ===

/**
 * Получить список FAQ с пагинацией и фильтрами
 */
export const useFAQList = (params?: {
  page?: number
  limit?: number
  search?: string
  include_inactive?: boolean
}) => {
  return useQuery({
    queryKey: ['faq-list', params],
    queryFn: () => AdminFAQService.getFAQList(params),
    staleTime: 5 * 60 * 1000, // 5 минут
  })
}

/**
 * Создать новый FAQ
 */
export const useCreateFAQ = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: AdminFAQService.createFAQ,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-list'] })
      toast.success('FAQ успешно создан')
    },
    onError: (error: ApiError) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка при создании FAQ')
      }
    },
  })
}

/**
 * Обновить FAQ
 */
export const useUpdateFAQ = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: FAQUpdateRequest }) =>
      AdminFAQService.updateFAQ(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-list'] })
      toast.success('FAQ успешно обновлен')
    },
    onError: (error: ApiError) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка при обновлении FAQ')
      }
    },
  })
}

/**
 * Удалить FAQ
 */
export const useDeleteFAQ = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: AdminFAQService.deleteFAQ,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-list'] })
      toast.success('FAQ успешно удален')
    },
    onError: (error: ApiError) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка при удалении FAQ')
      }
    },
  })
}
